/* Tool rail — icon rail with tooltips and shortcut hints.
 * Every action has a shortcut; no action requires one (Principle 7).
 */
import {
  BarChart3,
  Library,
  MapPin,
  MousePointer2,
  PenLine,
  Route,
  Ruler,
  Search,
  Target,
  Waypoints,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useDeck, type Tool } from '../state/store'

interface ToolDef {
  id: Tool
  icon: LucideIcon
  label: string
  key: string
}

const TOOLS: ToolDef[] = [
  { id: 'select', icon: MousePointer2, label: 'Select', key: 'V' },
  { id: 'course', icon: Route, label: 'Plot course', key: 'C' },
  { id: 'measure', icon: Ruler, label: 'Measure bearing / range', key: 'M' },
  { id: 'refpoint', icon: MapPin, label: 'Reference point', key: 'R' },
  { id: 'annotate', icon: PenLine, label: 'Annotate', key: 'A' },
]

type SecondaryAction = 'missions' | 'orbat' | 'scenario' | 'analysis'

const SECONDARY: { icon: LucideIcon; label: string; key: string; action?: SecondaryAction }[] = [
  { icon: Target, label: 'Missions', key: 'O', action: 'missions' },
  { icon: Waypoints, label: 'Operation planner', key: 'P' },
  { icon: Search, label: 'Order of battle — find unit or track', key: 'B', action: 'orbat' },
  // The library, and from it the ORBAT builder. It sits in the rail rather than behind a menu
  // because switching the exercise is the first thing anyone does in a demo, and hunting for it
  // is the first thing that goes wrong.
  { icon: Library, label: 'Scenario library — run or author an exercise', key: 'N',
    action: 'scenario' },
  // Y for analYsis: A is the annotate tool and the letters around it are all spoken for. The
  // panel is in the rail because "how often does this plan work?" is the question the product
  // exists to answer, and a viewer who cannot find it sees a map instead of an answer.
  { icon: BarChart3, label: 'Analysis — Range batch results', key: 'Y', action: 'analysis' },
]

export function ToolRail() {
  const tool = useDeck((s) => s.tool)
  const setTool = useDeck((s) => s.setTool)
  const missionsOpen = useDeck((s) => s.missionsOpen)
  const toggleMissions = useDeck((s) => s.toggleMissions)
  const orbatOpen = useDeck((s) => s.orbatOpen)
  const toggleOrbat = useDeck((s) => s.toggleOrbat)
  const scenarioOpen = useDeck((s) => s.scenarioOpen)
  const toggleScenario = useDeck((s) => s.toggleScenario)
  const analysisOpen = useDeck((s) => s.analysisOpen)
  const toggleAnalysis = useDeck((s) => s.toggleAnalysis)

  const isOpen: Record<SecondaryAction, boolean> = {
    missions: missionsOpen,
    orbat: orbatOpen,
    scenario: scenarioOpen,
    analysis: analysisOpen,
  }
  const toggleFor: Record<SecondaryAction, () => void> = {
    missions: toggleMissions,
    orbat: toggleOrbat,
    scenario: toggleScenario,
    analysis: toggleAnalysis,
  }

  return (
    <nav
      className="flex w-11 shrink-0 flex-col items-center gap-1 border-r py-2"
      style={{ background: 'var(--color-surface-1)', borderColor: 'var(--color-line-2)' }}
      aria-label="Tools"
    >
      {TOOLS.map(({ id, icon: Icon, label, key }) => (
        <button
          key={id}
          className="ctl"
          style={{ width: 32, height: 32, padding: 0 }}
          data-active={tool === id}
          onClick={() => setTool(id)}
          aria-label={`${label} (${key})`}
          aria-pressed={tool === id}
          title={`${label}  (${key})`}
        >
          <Icon size={16} />
        </button>
      ))}

      <div className="my-1 h-px w-6" style={{ background: 'var(--color-line-2)' }} />

      {SECONDARY.map(({ icon: Icon, label, key, action }) => (
        <button
          key={label}
          className="ctl"
          style={{ width: 32, height: 32, padding: 0 }}
          data-active={action ? isOpen[action] : undefined}
          onClick={action ? toggleFor[action] : undefined}
          aria-label={`${label} (${key})`}
          title={`${label}  (${key})`}
        >
          <Icon size={16} />
        </button>
      ))}
    </nav>
  )
}
